import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { User, Bell, MapPin, LogOut, Save, Ticket, BarChart3, Plus, RefreshCw, Edit3, Trash2, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api.service';
import { UpdateUserDto } from '../types/auth';
import { AvatarUpload } from './AvatarUpload';
import { toast } from 'sonner';
import { CreateInvitationCodeDto, InvitationCodeAnalytics, UpdateInvitationCodeDto } from '../types/invitation-code';


export function Settings() {
    const { user, logout } = useAuth();
    const isAdmin = user?.email === process.env.REACT_APP_ADMIN_EMAIL;
    const [showCreateCodeDialog, setShowCreateCodeDialog] = useState(false);
    const [showAnalyticsDialog, setShowAnalyticsDialog] = useState(false);
    const [showEditCodeDialog, setShowEditCodeDialog] = useState(false);
    const [selectedCodeForAnalytics, setSelectedCodeForAnalytics] = useState(null);
    const [editingCode, setEditingCode] = useState<UpdateInvitationCodeDto | null>(null);
    const [newCode, setNewCode] = useState<CreateInvitationCodeDto>({
        code: '',
        createdBy: user!.email,
        description: '',
        maxUses: 10,
        expiresAt: new Date(),
    });
    const [invitationCodes, setInvitationCodes] = useState<InvitationCodeAnalytics[]>([]);

    // Mock analytics data
    const [codeAnalytics, setCodeAnalytics] = useState({
        totalCodes: invitationCodes.length,
        activeCodes: invitationCodes.filter(code => code.isActive).length,
        totalUsage: invitationCodes.reduce((sum, code) => sum + code.usedCount, 0),
        usageToday: 5,
        usageThisWeek: 18,
        usageThisMonth: 35,
        recentUsage: [
            { date: '2025-01-20', code: 'WELCOME2025', user: 'user123@example.com' },
            { date: '2025-01-20', code: 'TENNIS50', user: 'tennis_fan@example.com' },
            { date: '2025-01-19', code: 'WELCOME2025', user: 'newuser@example.com' },
            { date: '2025-01-19', code: 'WELCOME2025', user: 'sports_lover@example.com' },
            { date: '2025-01-18', code: 'TENNIS50', user: 'active_player@example.com' }
        ]
    });

    const [profile, setProfile] = useState({
        name: user?.name,
        email: user?.email,
        location: user?.location,
        bio: user?.bio,
    });

    const [notifications, setNotifications] = useState({
        activityReminders: true,
        newActivities: true,
        messages: false,
        marketing: false
    });

    const [privacy, setPrivacy] = useState({
        showLocation: true,
        showActivities: true,
        allowMessages: true
    });


    const fetchInvitationCodes = async () => {
        if (!isAdmin) return;
        const codes = await apiService.getInvitationAnalytics();
        setInvitationCodes(codes.codeDetails);
    };

    useEffect(() => {
        fetchInvitationCodes();
    }, []);



    const handleProfileChange = (field: keyof typeof profile, value: string) => {
        setProfile(prev => ({ ...prev, [field]: value }));
    };

    const handleNotificationChange = (field: keyof typeof notifications, value: boolean) => {
        setNotifications(prev => ({ ...prev, [field]: value }));
    };

    const handlePrivacyChange = (field: keyof typeof privacy, value: boolean) => {
        setPrivacy(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        try {
            const updatedUser = await apiService.updateUserProfile(profile as UpdateUserDto);
            toast.success('Settings saved successfully!');
        } catch (error) {
            console.error('Failed to save settings:', error);
            toast.error('Failed to save settings. Please try again.');
        }
    };

    // Invitation Code Management Functions
    const generateRandomCode = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = '';
        for (let i = 0; i < 8; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    };

    const handleCreateCode = async (newCode: CreateInvitationCodeDto) => {
        if (!newCode.code.trim()) {
            toast.error('Please enter a code');
            return;
        }

        if (invitationCodes.some(code => code.code === newCode.code)) {
            toast.error('Code already exists');
            return;
        }

        const codeToCreate: CreateInvitationCodeDto = {
            code: newCode.code.toUpperCase(),
            createdBy: newCode.createdBy,
            description: newCode.description || 'No description',
            expiresAt: newCode.expiresAt ? new Date(newCode.expiresAt) : new Date(),
            maxUses: newCode.maxUses,
        };

        setInvitationCodes(prev => [newCode as InvitationCodeAnalytics, ...prev]);

        await apiService.createInvitationCode(codeToCreate);
        setShowCreateCodeDialog(false);
        toast.success('Invitation code created successfully!');
    };

    const handleToggleCodeStatus = (codeId: string) => {
        setInvitationCodes(prev =>
            prev.map(code =>
                code.id === codeId
                    ? { ...code, isActive: !code.isActive }
                    : code
            )
        );
        toast.success('Code status updated');
    };

    const handleDeleteCode = (codeId: string) => {
        setInvitationCodes(prev => prev.filter(code => code.id !== codeId));
        toast.success('Code deleted successfully');
    };

    const handleEditCode = (code: any) => {
        setEditingCode({
            ...code
        });
        setShowEditCodeDialog(true);
    };

    const handleUpdateCode = async () => {
        if (!editingCode) return;

        // Check if the new code already exists (excluding the current code being edited)
        if (invitationCodes.some(code => code.code === editingCode.code && code.id !== editingCode.id)) {
            toast.error('Code already exists');
            return;
        }

        if (!editingCode.code?.trim()) {
            toast.error('Please enter a code');
            return;
        }

        await apiService.updateInvitationCode({
            id: editingCode.id,
            code: editingCode.code?.toUpperCase() || '',
            description: editingCode.description || '',
            maxUses: editingCode.maxUses || 0,
            isActive: editingCode.isActive || false,
            expiresAt: editingCode.expiresAt ? new Date(editingCode.expiresAt) : new Date(),
        });

        setInvitationCodes(prev =>
            prev.map(code =>
                code.id === editingCode.id
                    ? {
                        ...code,
                        code: editingCode.code?.toUpperCase() || '',
                        description: editingCode.description || '',
                        maxUses: editingCode.maxUses || 0,
                        isActive: editingCode.isActive || false,
                        expiresAt: editingCode.expiresAt ? new Date(editingCode.expiresAt) : new Date(),
                    }
                    : code
            )
        );

        setEditingCode(null);
        setShowEditCodeDialog(false);
        toast.success('Invitation code updated successfully!');
    };

    const handleCopyCode = (code: string) => {
        navigator.clipboard.writeText(code);
        toast.success('Code copied to clipboard!');
    };

    const getCodeTypeColor = (type: string) => {
        switch (type) {
            case 'registration': return 'bg-blue-100 text-blue-800';
            case 'discount': return 'bg-green-100 text-green-800';
            case 'access': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusColor = (isActive: boolean, expiresAt: string) => {
        if (!isActive) return 'bg-red-100 text-red-800';
        if (new Date(expiresAt) < new Date()) return 'bg-yellow-100 text-yellow-800';
        return 'bg-green-100 text-green-800';
    };

    const getStatusText = (isActive: boolean, expiresAt: string) => {
        if (!isActive) return 'Inactive';
        if (new Date(expiresAt) < new Date()) return 'Expired';
        return 'Active';
    };

    return (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold mb-2">Settings</h1>
                <p className="text-muted-foreground">
                    Manage your account and preferences
                </p>
            </div>

            <div className="space-y-6">
                {/* Profile Settings */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center space-x-2">
                            <User size={20} />
                            <span>Profile Information</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <h3 className="text-lg font-medium">Profile Picture</h3>
                                <AvatarUpload
                                    currentAvatar={user?.avatar}
                                    onAvatarUpdated={(newUrl) => {
                                        // Handle avatar update
                                        console.log('Avatar updated:', newUrl);
                                    }}
                                />
                            </div>
                            <div>
                                <Label htmlFor="name">Full Name</Label>
                                <Input
                                    id="name"
                                    value={profile.name}
                                    onChange={(e) => handleProfileChange('name', e.target.value)}
                                />
                            </div>
                            <div>
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={profile.email}
                                    onChange={(e) => handleProfileChange('email', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <Label htmlFor="location">Location</Label>
                                <Input
                                    id="location"
                                    value={profile.location}
                                    onChange={(e) => handleProfileChange('location', e.target.value)}
                                />
                            </div>

                            <div className="md:col-span-2">
                                <Label htmlFor="bio">Bio</Label>
                                <textarea
                                    id="bio"
                                    className="w-full px-3 py-2 border border-input rounded-md text-sm"
                                    rows={3}
                                    value={profile.bio}
                                    onChange={(e) => handleProfileChange('bio', e.target.value)}
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Notification Settings */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center space-x-2">
                            <Bell size={20} />
                            <span>Notifications</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <Label>Activity Reminders</Label>
                                <p className="text-sm text-muted-foreground">
                                    Get reminded about upcoming activities
                                </p>
                            </div>
                            <Switch
                                checked={notifications.activityReminders}
                                onCheckedChange={(checked: boolean) => handleNotificationChange('activityReminders', checked)}
                            />
                        </div>

                        <Separator />

                        <div className="flex items-center justify-between">
                            <div>
                                <Label>New Activities</Label>
                                <p className="text-sm text-muted-foreground">
                                    Notifications about new activities in your area
                                </p>
                            </div>
                            <Switch
                                checked={notifications.newActivities}
                                onCheckedChange={(checked: boolean) => handleNotificationChange('newActivities', checked)}
                            />
                        </div>

                        <Separator />

                        <div className="flex items-center justify-between">
                            <div>
                                <Label>Messages</Label>
                                <p className="text-sm text-muted-foreground">
                                    Messages from other users
                                </p>
                            </div>
                            <Switch
                                checked={notifications.messages}
                                onCheckedChange={(checked: boolean) => handleNotificationChange('messages', checked)}
                            />
                        </div>

                        <Separator />

                        <div className="flex items-center justify-between">
                            <div>
                                <Label>Marketing</Label>
                                <p className="text-sm text-muted-foreground">
                                    Promotional emails and updates
                                </p>
                            </div>
                            <Switch
                                checked={notifications.marketing}
                                onCheckedChange={(checked: boolean) => handleNotificationChange('marketing', checked)}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Privacy Settings */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center space-x-2">
                            <MapPin size={20} />
                            <span>Privacy</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <Label>Show Location</Label>
                                <p className="text-sm text-muted-foreground">
                                    Allow others to see your approximate location
                                </p>
                            </div>
                            <Switch
                                checked={privacy.showLocation}
                                onCheckedChange={(checked: boolean) => handlePrivacyChange('showLocation', checked)}
                            />
                        </div>

                        <Separator />

                        <div className="flex items-center justify-between">
                            <div>
                                <Label>Show Activities</Label>
                                <p className="text-sm text-muted-foreground">
                                    Show your joined activities to other users
                                </p>
                            </div>
                            <Switch
                                checked={privacy.showActivities}
                                onCheckedChange={(checked: boolean) => handlePrivacyChange('showActivities', checked)}
                            />
                        </div>

                        <Separator />

                        <div className="flex items-center justify-between">
                            <div>
                                <Label>Allow Messages</Label>
                                <p className="text-sm text-muted-foreground">
                                    Allow other users to send you messages
                                </p>
                            </div>
                            <Switch
                                checked={privacy.allowMessages}
                                onCheckedChange={(checked: boolean) => handlePrivacyChange('allowMessages', checked)}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Invitation Code Management - Admin Only */}
                {isAdmin && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <Ticket size={20} />
                                    <span>Invitation Code Management</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Dialog open={showAnalyticsDialog} onOpenChange={setShowAnalyticsDialog}>
                                        <DialogTrigger asChild>
                                            <Button variant="outline" size="sm">
                                                <BarChart3 size={16} className="mr-1" />
                                                Analytics
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="max-w-4xl">
                                            <DialogHeader>
                                                <DialogTitle>Invitation Code Analytics</DialogTitle>
                                            </DialogHeader>
                                            <div className="space-y-6">
                                                {/* Analytics Overview */}
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                                                        <p className="text-2xl font-bold text-blue-600">{codeAnalytics.totalCodes}</p>
                                                        <p className="text-sm text-blue-800">Total Codes</p>
                                                    </div>
                                                    <div className="text-center p-4 bg-green-50 rounded-lg">
                                                        <p className="text-2xl font-bold text-green-600">{codeAnalytics.activeCodes}</p>
                                                        <p className="text-sm text-green-800">Active Codes</p>
                                                    </div>
                                                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                                                        <p className="text-2xl font-bold text-purple-600">{codeAnalytics.totalUsage}</p>
                                                        <p className="text-sm text-purple-800">Total Usage</p>
                                                    </div>
                                                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                                                        <p className="text-2xl font-bold text-orange-600">{codeAnalytics.usageToday}</p>
                                                        <p className="text-sm text-orange-800">Usage Today</p>
                                                    </div>
                                                </div>

                                                {/* Usage Timeline */}
                                                <div className="space-y-3">
                                                    <h3 className="font-medium">Usage Statistics</h3>
                                                    <div className="grid grid-cols-3 gap-4 text-center">
                                                        <div className="p-3 bg-accent/50 rounded">
                                                            <p className="text-lg font-semibold">{codeAnalytics.usageToday}</p>
                                                            <p className="text-xs text-muted-foreground">Today</p>
                                                        </div>
                                                        <div className="p-3 bg-accent/50 rounded">
                                                            <p className="text-lg font-semibold">{codeAnalytics.usageThisWeek}</p>
                                                            <p className="text-xs text-muted-foreground">This Week</p>
                                                        </div>
                                                        <div className="p-3 bg-accent/50 rounded">
                                                            <p className="text-lg font-semibold">{codeAnalytics.usageThisMonth}</p>
                                                            <p className="text-xs text-muted-foreground">This Month</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Recent Usage */}
                                                <div className="space-y-3">
                                                    <h3 className="font-medium">Recent Usage</h3>
                                                    <div className="space-y-2">
                                                        {codeAnalytics.recentUsage.map((usage, index) => (
                                                            <div key={index} className="flex items-center justify-between p-3 bg-accent/30 rounded">
                                                                <div className="flex items-center space-x-3">
                                                                    <Badge variant="outline" className="font-mono text-xs">
                                                                        {usage.code}
                                                                    </Badge>
                                                                    <span className="text-sm">{usage.user}</span>
                                                                </div>
                                                                <span className="text-xs text-muted-foreground">{usage.date}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </DialogContent>
                                    </Dialog>

                                    <Dialog open={showCreateCodeDialog} onOpenChange={setShowCreateCodeDialog}>
                                        <DialogTrigger asChild>
                                            <Button size="sm">
                                                <Plus size={16} className="mr-1" />
                                                Create Code
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Create New Invitation Code</DialogTitle>
                                            </DialogHeader>
                                            <div className="space-y-4">
                                                <div className="flex items-center space-x-2">
                                                    <div className="flex-1">
                                                        <Label htmlFor="newCodeInput">Invitation Code</Label>
                                                        <Input
                                                            id="newCodeInput"
                                                            placeholder="Enter code or leave blank to generate"
                                                            value={newCode.code}
                                                            onChange={(e) => setNewCode(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                                                            className="font-mono"
                                                        />
                                                    </div>
                                                    <Button
                                                        variant="outline"
                                                        onClick={() => setNewCode(prev => ({ ...prev, code: generateRandomCode() }))}
                                                        className="mt-6"
                                                    >
                                                        <RefreshCw size={16} />
                                                    </Button>
                                                </div>

                                                <div>
                                                    <Label htmlFor="description">Description</Label>
                                                    <Input
                                                        id="description"
                                                        placeholder="What is this code for?"
                                                        value={newCode.description}
                                                        onChange={(e) => setNewCode(prev => ({ ...prev, description: e.target.value }))}
                                                    />
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <Label htmlFor="codeType">Code Type</Label>
                                                        <Select
                                                            value={newCode.maxUses.toString()}
                                                            onValueChange={(value) => setNewCode(prev => ({ ...prev, maxUses: parseInt(value) || 1 }))}
                                                        >
                                                            <SelectTrigger>
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="registration">Registration</SelectItem>
                                                                <SelectItem value="discount">Discount</SelectItem>
                                                                <SelectItem value="access">Special Access</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    <div>
                                                        <Label htmlFor="usageLimit">Usage Limit</Label>
                                                        <Input
                                                            id="usageLimit"
                                                            type="number"
                                                            min={1}
                                                            value={newCode.maxUses}
                                                            onChange={(e) => setNewCode(prev => ({ ...prev, maxUses: parseInt(e.target.value) || 1 }))}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <Label htmlFor="expiresAt">Expiration Date</Label>
                                                    <Input
                                                        id="expiresAt"
                                                        type="date"
                                                        value={newCode.expiresAt ? new Date(newCode.expiresAt).toISOString().split('T')[0] : ''}
                                                        onChange={(e) => setNewCode(prev => ({ ...prev, expiresAt: new Date(e.target.value) }))}
                                                    />
                                                </div>

                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-2">
                                                        <Switch
                                                            checked={true}
                                                            onCheckedChange={(checked) => setNewCode(prev => ({ ...prev, isActive: checked }))}
                                                        />
                                                        <Label>Active immediately</Label>
                                                    </div>

                                                    <Button onClick={() => handleCreateCode(newCode)}>
                                                        Create Code
                                                    </Button>
                                                </div>
                                            </div>
                                        </DialogContent>
                                    </Dialog>

                                    {/* Edit Code Dialog */}
                                    <Dialog open={showEditCodeDialog} onOpenChange={setShowEditCodeDialog}>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Edit Invitation Code</DialogTitle>
                                            </DialogHeader>
                                            {editingCode && (
                                                <div className="space-y-4">
                                                    <div className="flex items-center space-x-2">
                                                        <div className="flex-1">
                                                            <Label htmlFor="editCodeInput">Invitation Code</Label>
                                                            <Input
                                                                id="editCodeInput"
                                                                placeholder="Enter code"
                                                                value={editingCode.code}
                                                                onChange={(e) => setEditingCode(prev => ({ ...prev, code: e.target.value.toUpperCase() } as InvitationCodeAnalytics))}
                                                                className="font-mono"
                                                            />
                                                        </div>
                                                        <Button
                                                            variant="outline"
                                                            onClick={() => setEditingCode(prev => ({ ...prev, code: generateRandomCode() } as InvitationCodeAnalytics))}
                                                            className="mt-6"
                                                        >
                                                            <RefreshCw size={16} />
                                                        </Button>
                                                    </div>

                                                    <div>
                                                        <Label htmlFor="editDescription">Description</Label>
                                                        <Input
                                                            id="editDescription"
                                                            placeholder="What is this code for?"
                                                            value={editingCode.description}
                                                            onChange={(e) => setEditingCode(prev => ({ ...prev, description: e.target.value } as InvitationCodeAnalytics))}
                                                        />
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <Label htmlFor="editCodeType">Code Type</Label>
                                                            <Select
                                                                value={editingCode.maxUses?.toString() || '1'}
                                                                onValueChange={(value) => setEditingCode(prev => ({ ...prev, maxUses: parseInt(value) || 1 } as InvitationCodeAnalytics))}
                                                            >
                                                                <SelectTrigger>
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="registration">Registration</SelectItem>
                                                                    <SelectItem value="discount">Discount</SelectItem>
                                                                    <SelectItem value="access">Special Access</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>

                                                        <div>
                                                            <Label htmlFor="editUsageLimit">Usage Limit</Label>
                                                            <Input
                                                                id="editUsageLimit"
                                                                type="number"
                                                                min="1"
                                                                value={editingCode.maxUses}
                                                                onChange={(e) => setEditingCode(prev => ({ ...prev, maxUses: parseInt(e.target.value) || 1 } as InvitationCodeAnalytics))}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <Label htmlFor="editExpiresAt">Expiration Date</Label>
                                                        <Input
                                                            id="editExpiresAt"
                                                            type="date"
                                                            value={new Date(editingCode.expiresAt || '').toISOString().split('T')[0]}
                                                            onChange={(e) => setEditingCode(prev => ({ ...prev, expiresAt: new Date(e.target.value) } as InvitationCodeAnalytics))}
                                                        />
                                                    </div>

                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-2">
                                                            <Switch
                                                                checked={editingCode.isActive}
                                                                onCheckedChange={(checked) => setEditingCode(prev => ({ ...prev, isActive: checked } as InvitationCodeAnalytics))}
                                                            />
                                                            <Label>Active</Label>
                                                        </div>

                                                        <div className="flex space-x-2">
                                                            <Button
                                                                variant="outline"
                                                                onClick={() => {
                                                                    setEditingCode(null);
                                                                    setShowEditCodeDialog(false);
                                                                }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                            <Button onClick={handleUpdateCode}>
                                                                Update Code
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </DialogContent>
                                    </Dialog>
                                </div>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Codes List */}
                            <div className="space-y-3">
                                <h3 className="font-medium">Active Invitation Codes</h3>

                                {invitationCodes.length > 0 ? (
                                    <div className="space-y-3">
                                        {invitationCodes.map((code) => (
                                            <div key={code.id} className="p-4 border border-border rounded-lg hover:bg-accent/30 transition-colors">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center space-x-3 mb-2">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleCopyCode(code.code as string)}
                                                                className="font-mono text-lg font-bold p-1 h-auto hover:bg-accent"
                                                            >
                                                                {code.code}
                                                                <Copy size={14} className="ml-2" />
                                                            </Button>

                                                            <Badge className={`text-xs px-2 py-1 ${getCodeTypeColor(code.maxUses.toString())}`}>
                                                                {code.maxUses}
                                                            </Badge>

                                                            <Badge className={`text-xs px-2 py-1 ${getStatusColor(code.isActive, new Date(code.expiresAt || '').toISOString())}`}>
                                                                {getStatusText(code.isActive, new Date(code.expiresAt || '').toISOString())}
                                                            </Badge>
                                                        </div>

                                                        <p className="text-sm text-muted-foreground mb-2">{code.description}</p>

                                                        <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                                                            <span>Usage: {code.usedCount}/{code.maxUses}</span>
                                                            <span>Expires: {code.expiresAt ? new Date(code.expiresAt || '').toLocaleDateString() : 'Never'}</span>
                                                            <span>Created: {new Date(code.createdAt).toLocaleDateString()}</span>
                                                        </div>

                                                        {/* Usage Progress Bar */}
                                                        <div className="mt-2">
                                                            <div className="w-full bg-accent rounded-full h-1.5">
                                                                <div
                                                                    className="bg-primary h-1.5 rounded-full transition-all duration-300"
                                                                    style={{ width: `${Math.min((code.usedCount / code.maxUses) * 100, 100)}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center space-x-2 ml-4">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleToggleCodeStatus(code.id as string)}
                                                            className={code.isActive ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                                                        >
                                                            {code.isActive ? 'Deactivate' : 'Activate'}
                                                        </Button>

                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleEditCode(code as InvitationCodeAnalytics)}
                                                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                                        >
                                                            <Edit3 size={16} />
                                                        </Button>

                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDeleteCode(code.id as string)}
                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <Ticket className="mx-auto mb-2" size={48} />
                                        <p>No invitation codes created yet</p>
                                        <p className="text-sm">Create your first invitation code to get started</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Actions */}
                <div className="flex justify-between items-center pt-6">
                    <Button onClick={handleSave} className="flex items-center space-x-2">
                        <Save size={16} />
                        <span>Save Changes</span>
                    </Button>

                    <Button
                        variant="destructive"
                        onClick={() => logout()}
                        className="flex items-center space-x-2"
                    >
                        <LogOut size={16} />
                        <span>Logout</span>
                    </Button>
                </div>
            </div>
        </div>
    );
}